/**
 * DropdownMenu — shadcn-style wrapper around Radix DropdownMenu.
 *
 * Used for filter pills (Health / Sort), row-action menus, and any
 * "command-style" menu that's discoverable but not always visible.
 *
 * Visual: same hairline-bordered card surface as Card / Dialog, with item
 * focus and active states pulled from the brand spec (sky-50 / ink-2).
 */
import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight } from "lucide-react";
import { cn } from "../lib/cn.js";

export const DropdownMenuRoot = DropdownMenu.Root;
export const DropdownMenuTrigger = DropdownMenu.Trigger;
export const DropdownMenuPortal = DropdownMenu.Portal;
export const DropdownMenuGroup = DropdownMenu.Group;
export const DropdownMenuRadioGroup = DropdownMenu.RadioGroup;
export const DropdownMenuSub = DropdownMenu.Sub;

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <DropdownMenuPortal>
      <DropdownMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-[10rem] overflow-hidden rounded-md border border-line bg-paper p-1 shadow-lg outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          className,
        )}
        {...props}
      />
    </DropdownMenuPortal>
  );
});

const ITEM_BASE =
  "relative flex select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-ink-2 outline-none cursor-pointer " +
  "data-[highlighted]:bg-accent-subtle data-[highlighted]:text-accent-hover data-[disabled]:opacity-50 data-[disabled]:pointer-events-none";

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.Item>
>(function DropdownMenuItem({ className, ...props }, ref) {
  return <DropdownMenu.Item ref={ref} className={cn(ITEM_BASE, className)} {...props} />;
});

export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.RadioItem ref={ref} className={cn(ITEM_BASE, "pl-7", className)} {...props}>
      <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <Check size={12} strokeWidth={2.5} aria-hidden />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.RadioItem>
  );
});

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.CheckboxItem ref={ref} className={cn(ITEM_BASE, "pl-7", className)} {...props}>
      <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <Check size={12} strokeWidth={2.5} aria-hidden />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.CheckboxItem>
  );
});

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenu.Label
      ref={ref}
      className={cn(
        "px-2 py-1.5 text-xs uppercase tracking-wider text-muted font-medium",
        className,
      )}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenu.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-line", className)}
      {...props}
    />
  );
});

export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.SubTrigger>
>(function DropdownMenuSubTrigger({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.SubTrigger ref={ref} className={cn(ITEM_BASE, className)} {...props}>
      {children}
      <ChevronRight size={12} strokeWidth={2} className="ml-auto opacity-70" aria-hidden />
    </DropdownMenu.SubTrigger>
  );
});

export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.SubContent>
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <DropdownMenu.SubContent
      ref={ref}
      className={cn(
        "z-50 min-w-[10rem] overflow-hidden rounded-md border border-line bg-paper p-1 shadow-lg outline-none",
        className,
      )}
      {...props}
    />
  );
});
